import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { commercialAPI } from '../services/api';

const REFERRAL_LABELS = {
  click: 'QR scanne / lien ouvert',
  install_open: 'Page installation ouverte',
  register_start: 'Inscription demarree',
  register_complete: 'Compte cree',
  child_created: 'Enfant ajoute',
  tracker_added: 'Traceur ajoute',
  tracker_associated: 'Traceur associe',
  subscription_purchase: 'Abonnement initie',
  subscription_paid: 'Abonnement paye',
  sms_purchase: 'Pack SMS initie',
  sms_paid: 'Pack SMS paye',
};

const STATUS_LABELS = {
  new: 'Nouveau',
  contacted: 'Contacte',
  demo: 'Demo faite',
  installed: 'Installe',
  activated: 'Active',
  paid: 'Payant',
  lost: 'Perdu',
};

const EMPLOYEE_BADGE_ROLES = [
  { value: 'direction', label: 'Direction', color: '#f5a623' },
  { value: 'administration', label: 'Administration', color: '#38bdf8' },
  { value: 'finance', label: 'Finance', color: '#22c55e' },
  { value: 'technique', label: 'Technique', color: '#60a5fa' },
  { value: 'support', label: 'Support client', color: '#a78bfa' },
  { value: 'commercial', label: 'Commercial', color: '#12a86b' },
  { value: 'terrain', label: 'Terrain', color: '#f97316' },
  { value: 'operations', label: 'Operations', color: '#14b8a6' },
  { value: 'partenaire', label: 'Partenaire', color: '#eab308' },
  { value: 'securite', label: 'Securite', color: '#ef4444' },
];

const TARGET_LABELS = {
  parent: 'Parent',
  school: 'Ecole',
  business: 'Entreprise',
  shop: 'Boutique',
  institution: 'Institution',
  other: 'Autre',
};

const REFERRAL_EVENT_LIMIT_OPTIONS = [5, 10, 20, 50, 100];

const EMPTY_AGENT_FORM = {
  code: '',
  fullName: '',
  phoneNumber: '',
  city: '',
  supervisor: '',
  status: 'active',
  photoUrl: '',
  photoDataUrl: '',
  removePhoto: false,
};

function agentPhotoInitials(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'AG';
}

function employeeBadgeInitials(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'TK';
}

function employeeRoleConfig(role) {
  return EMPLOYEE_BADGE_ROLES.find((item) => item.value === role) || EMPLOYEE_BADGE_ROLES[0];
}

function escapeBadgeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(value) {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatRate(value, total) {
  const numerator = Number(value) || 0;
  const denominator = Number(total) || 0;
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function StatCard({ label, value, hint, tone = 'blue' }) {
  return (
    <div style={S.statCard}>
      <div style={S.statLabel}>{label}</div>
      <div style={{ ...S.statValue, color: tone === 'gold' ? '#f5a623' : tone === 'green' ? '#22c55e' : '#ffffff' }}>{value ?? 0}</div>
      {hint && <div style={S.statHint}>{hint}</div>}
    </div>
  );
}

export default function CommercialPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ summary: {}, agents: [], recentLeads: [], recentReferralEvents: [], statusBreakdown: [], cityBreakdown: [] });
  const [referralEventSearch, setReferralEventSearch] = useState('');
  const [referralEventLimit, setReferralEventLimit] = useState(10);
  const [referralEventPage, setReferralEventPage] = useState(1);
  const [referralEventPagination, setReferralEventPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1, hasNext: false, hasPrevious: false });
  const [debouncedReferralEventSearch, setDebouncedReferralEventSearch] = useState('');
  const [referralEventsLoading, setReferralEventsLoading] = useState(false);
  const [agentForm, setAgentForm] = useState(EMPTY_AGENT_FORM);
  const [leadForm, setLeadForm] = useState({ agentCode: '', targetType: 'parent', prospectName: '', phoneNumber: '', city: '', neighborhood: '', source: '', interestLevel: 'warm', status: 'new', notes: '' });
  const [employeeBadgeForm, setEmployeeBadgeForm] = useState({
    fullName: '',
    role: 'commercial',
    department: '',
    phoneNumber: '',
    employeeId: '',
    city: 'Yaounde',
    photoDataUrl: '',
  });

  const agents = data.agents || [];
  const recentLeads = data.recentLeads || [];
  const recentReferralEvents = data.recentReferralEvents || [];
  const summary = data.summary || {};
  const activeAgents = useMemo(() => agents.filter((agent) => agent.status === 'active'), [agents]);

  async function loadDashboard() {
    setLoading(true);
    try {
      const { data: result } = await commercialAPI.dashboard();
      if (!result.ok) throw new Error(result.error || 'Chargement impossible');
      setData(result);
      if (!leadForm.agentCode && result.agents?.[0]?.code) {
        setLeadForm((current) => ({ ...current, agentCode: result.agents[0].code }));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur dashboard commercial');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDashboard(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReferralEventSearch(referralEventSearch.trim());
      setReferralEventPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [referralEventSearch]);

  useEffect(() => {
    let cancelled = false;
    async function loadReferralEvents() {
      setReferralEventsLoading(true);
      try {
        const { data: result } = await commercialAPI.referralEvents({ limit: referralEventLimit, search: debouncedReferralEventSearch, page: referralEventPage });
        if (!result.ok) throw new Error(result.error || 'Chargement des evenements impossible');
        if (!cancelled) {
          setData((current) => ({ ...current, recentReferralEvents: result.events || [] }));
          setReferralEventPagination({
            page: result.page || referralEventPage,
            limit: result.limit || referralEventLimit,
            total: result.total || 0,
            totalPages: result.totalPages || 1,
            hasNext: Boolean(result.hasNext),
            hasPrevious: Boolean(result.hasPrevious),
          });
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.error || error.message || 'Erreur evenements QR');
      } finally {
        if (!cancelled) setReferralEventsLoading(false);
      }
    }
    loadReferralEvents();
    return () => { cancelled = true; };
  }, [debouncedReferralEventSearch, referralEventLimit, referralEventPage]);

  function readAgentPhoto(file) {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Choisissez une photo JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo trop lourde. Maximum: 5 Mo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 600;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        const side = Math.min(image.width, image.height);
        const sx = Math.max(0, (image.width - side) / 2);
        const sy = Math.max(0, (image.height - side) / 2);
        ctx.drawImage(image, sx, sy, side, side, 0, 0, size, size);
        setAgentForm((current) => ({
          ...current,
          photoDataUrl: canvas.toDataURL('image/jpeg', 0.84),
          removePhoto: false,
        }));
      };
      image.onerror = () => toast.error('Photo illisible.');
      image.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  }

  function readEmployeeBadgePhoto(file) {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Choisissez une image JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Photo trop lourde. Maximum recommande: 4 Mo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 720;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        const side = Math.min(image.width, image.height);
        const sx = Math.max(0, (image.width - side) / 2);
        const sy = Math.max(0, (image.height - side) / 2);
        ctx.drawImage(image, sx, sy, side, side, 0, 0, size, size);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.84);
        setEmployeeBadgeForm((current) => ({ ...current, photoDataUrl: dataUrl }));
      };
      image.onerror = () => toast.error('Photo illisible.');
      image.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  }

  function printEmployeeBadge() {
    const fullName = employeeBadgeForm.fullName.trim();
    if (!fullName) {
      toast.error('Renseignez le nom de l employe.');
      return;
    }
    const role = employeeRoleConfig(employeeBadgeForm.role);
    const name = escapeBadgeHtml(fullName);
    const department = escapeBadgeHtml(employeeBadgeForm.department || role.label);
    const city = escapeBadgeHtml(employeeBadgeForm.city || 'TOKKAM');
    const phone = escapeBadgeHtml(employeeBadgeForm.phoneNumber || '');
    const employeeId = escapeBadgeHtml(employeeBadgeForm.employeeId || '');
    const photo = employeeBadgeForm.photoDataUrl;
    const initials = employeeBadgeInitials(fullName);
    const color = role.color;
    const roleLabel = escapeBadgeHtml(role.label);
    const win = window.open('', '_blank', 'width=760,height=860');
    if (!win) {
      toast.error('Autorisez les popups pour generer le badge.');
      return;
    }
    win.document.write(`<!doctype html>
      <html lang="fr">
        <head>
          <meta charset="utf-8" />
          <title>Badge employe TOKKAM</title>
          <style>
            * { box-sizing: border-box; }
            html, body { margin: 0; min-height: 100%; background: #e8edf5; font-family: Arial, sans-serif; color: #061833; }
            .toolbar { position: sticky; top: 0; z-index: 20; display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; padding: 14px; background: rgba(232,237,245,.96); border-bottom: 1px solid #cbd5e1; }
            .toolbar button { border: 0; border-radius: 8px; padding: 10px 14px; font-weight: 900; cursor: pointer; }
            .toolbar .primary { background: #0d1b3e; color: #fff; }
            .toolbar .secondary { background: ${color}; color: #061833; }
            .toolbar .light { background: #fff; color: #0d1b3e; border: 1px solid #cbd5e1; }
            .hint { width: 100%; text-align: center; color: #475569; font-size: 12px; }
            .sheet { display: grid; grid-template-columns: repeat(2, 54mm); place-content: center; align-items: start; gap: 18px; padding: 22px; }
            .badge { width: 54mm; height: 85.6mm; border-radius: 3.2mm; overflow: hidden; position: relative; box-shadow: 0 18px 45px rgba(0,0,0,.24); border: .35mm solid ${color}; }
            .front { background: linear-gradient(145deg, #07162f 0%, #0d1b3e 62%, #123d5a 100%); color: #fff; }
            .front .accent { position: absolute; left: -12mm; right: -14mm; bottom: -15mm; height: 27mm; background: ${color}; opacity: .96; transform: rotate(-8deg); border-radius: 50%; }
            .front .top { position: relative; z-index: 1; display: grid; justify-items: center; gap: 1.2mm; padding: 6mm 4.5mm 0; text-align: center; }
            .brand { font-weight: 950; font-size: 6.4mm; line-height: .95; }
            .role { color: #fff; background: rgba(255,255,255,.12); border: .2mm solid rgba(255,255,255,.28); border-radius: 10mm; padding: 1.25mm 3mm; font-size: 2.15mm; font-weight: 950; line-height: 1; text-align: center; max-width: 42mm; white-space: nowrap; text-transform: uppercase; }
            .tag { color: #f5a623; font-size: 1.85mm; font-weight: 900; text-transform: uppercase; line-height: 1.15; max-width: 42mm; text-align: center; }
            .front .content { position: relative; z-index: 1; display: grid; justify-items: center; gap: 1.55mm; padding: 4.2mm 4.5mm 0; text-align: center; }
            .photo { width: 25mm; height: 29mm; border-radius: 4mm; overflow: hidden; background: #fff; color: #0d1b3e; display: grid; place-items: center; font-size: 7.4mm; font-weight: 950; border: .6mm solid rgba(255,255,255,.8); }
            .photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
            .name { font-size: 3.45mm; line-height: 1.02; font-weight: 950; text-transform: uppercase; max-width: 43mm; text-wrap: balance; }
            .dept { color: rgba(255,255,255,.88); font-size: 2.3mm; font-weight: 850; margin-top: .35mm; }
            .id { display: inline-block; background: ${color}; color: #07162f; border-radius: 2mm; padding: 1mm 2.2mm; font-size: 2.65mm; font-weight: 950; letter-spacing: .12mm; margin-top: .65mm; max-width: 40mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .front .bottom { position: absolute; z-index: 2; left: 4mm; right: 4mm; bottom: 3mm; display: grid; gap: 1.1mm; text-align: center; font-size: 2.18mm; line-height: 1.05; font-weight: 900; color: #fff; }
            .cityBottom, .phoneBottom { max-width: 42mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .back { background: #fff; color: #0d1b3e; border-color: #0d1b3e; display: grid; justify-items: center; align-content: center; gap: 3.3mm; padding: 6mm 5mm; text-align: center; }
            .back .brandSmall { font-size: 5.2mm; font-weight: 950; color: #0d1b3e; }
            .back h2 { margin: 0; font-size: 4.5mm; line-height: 1.05; color: #0d1b3e; }
            .back p { margin: 0; font-size: 2.65mm; line-height: 1.22; color: #23364d; }
            .rules { text-align: left; font-size: 2.25mm; line-height: 1.45; color: #23364d; border-top: .2mm solid #d8dee9; padding-top: 3mm; width: 100%; }
            .only-front, .only-back { display: contents; }
            @page { size: 54mm 85.6mm; margin: 0; }
            @media print {
              html, body { width: 54mm; min-height: 85.6mm; background: #fff; }
              .toolbar { display: none; }
              .sheet { display: block; padding: 0; }
              .badge { box-shadow: none; border-radius: 0; border: 0; break-after: page; page-break-after: always; }
              body.print-front .only-back { display: none; }
              body.print-back .only-front { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <button class="primary" onclick="document.body.className='print-front'; window.print()">Imprimer recto</button>
            <button class="secondary" onclick="document.body.className='print-back'; window.print()">Imprimer verso</button>
            <button class="light" onclick="document.body.className='print-all'; window.print()">Imprimer les deux</button>
            <button class="light" onclick="window.close()">Fermer</button>
            <div class="hint">Format PVC CR80 portrait: recto d'abord, puis verso apres retournement des cartes.</div>
          </div>
          <div class="sheet">
            <div class="only-front">
              <section class="badge front">
                <div class="accent"></div>
                <div class="top">
                  <div class="brand">TOKKAM</div>
                  <div class="role">${roleLabel}</div>
                  <div class="tag">Protect · Connect · Reassure</div>
                </div>
                <div class="content">
                  <div class="photo">${photo ? `<img src="${photo}" alt="Photo ${name}" />` : initials}</div>
                  <div>
                    <div class="name">${name}</div>
                    <div class="dept">${department}</div>
                    <div class="id">${employeeId || roleLabel}</div>
                  </div>
                </div>
                <div class="bottom">
                  <div class="cityBottom">${city}</div>
                  <div class="phoneBottom">${phone || 'TOKKAM'}</div>
                </div>
              </section>
            </div>
            <div class="only-back">
              <section class="badge back">
                <div class="brandSmall">TOKKAM</div>
                <h2>Badge professionnel</h2>
                <p>Ce badge identifie un membre autorise de l'equipe TOKKAM.</p>
                <p><strong>${name}</strong><br/>${roleLabel} · ${department}</p>
                <div class="rules">
                  <strong>Utilisation</strong><br/>
                  - Badge strictement personnel.<br/>
                  - A presenter lors des activites officielles TOKKAM.<br/>
                  - En cas de perte, informer immediatement l'administration.
                </div>
              </section>
            </div>
          </div>
        </body>
      </html>`);
    win.document.close();
  }

  async function saveAgent(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const { data: result } = await commercialAPI.saveAgent(agentForm);
      if (!result.ok) throw new Error(result.error || 'Enregistrement impossible');
      toast.success('Agent enregistre');
      setAgentForm(EMPTY_AGENT_FORM);
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur agent');
    } finally {
      setSaving(false);
    }
  }

  async function saveLead(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const { data: result } = await commercialAPI.saveLead(leadForm);
      if (!result.ok) throw new Error(result.error || 'Enregistrement impossible');
      toast.success('Prospect enregistre');
      setLeadForm((current) => ({ ...current, prospectName: '', phoneNumber: '', neighborhood: '', source: '', notes: '', status: 'new' }));
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur prospect');
    } finally {
      setSaving(false);
    }
  }

  async function updateLeadStatus(lead, status) {
    try {
      const { data: result } = await commercialAPI.updateLeadStatus({ id: lead.id, status });
      if (!result.ok) throw new Error(result.error || 'Mise a jour impossible');
      toast.success('Statut mis a jour');
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur statut');
    }
  }

  function installLink(agentCode) {
    return `https://tokkam.net/install?agent=${encodeURIComponent(agentCode || '')}`;
  }

  async function copyInstallLink(agentCode) {
    const link = installLink(agentCode);
    try {
      await navigator.clipboard?.writeText(link);
      toast.success('Lien copie');
    } catch (_error) {
      toast(link);
    }
  }

  function qrUrl(agentCode, size = 160) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(installLink(agentCode))}`;
  }


  function printPvcBadge(agent) {
    const link = installLink(agent.code);
    const qr = qrUrl(agent.code, 420);
    const name = agent.full_name || agent.fullName || agent.code;
    const city = agent.city || 'TOKKAM';
    const phone = agent.phone_number || agent.phoneNumber || '';
    const photo = agent.photo_url || agent.photoUrl || '';
    const initials = agentPhotoInitials(name);
    const win = window.open('', '_blank', 'width=760,height=860');
    if (!win) {
      toast.error('Autorisez les popups pour generer le badge PVC.');
      return;
    }
    win.document.write(`<!doctype html>
      <html lang="fr">
        <head>
          <meta charset="utf-8" />
          <title>Badge PVC ${agent.code}</title>
          <style>
            * { box-sizing: border-box; }
            html, body { margin: 0; min-height: 100%; background: #e8edf5; font-family: Arial, sans-serif; color: #061833; }
            .toolbar { position: sticky; top: 0; z-index: 20; display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; padding: 14px; background: rgba(232,237,245,.96); border-bottom: 1px solid #cbd5e1; }
            .toolbar button { border: 0; border-radius: 8px; padding: 10px 14px; font-weight: 900; cursor: pointer; }
            .toolbar .primary { background: #0d1b3e; color: #fff; }
            .toolbar .secondary { background: #12a86b; color: #fff; }
            .toolbar .light { background: #fff; color: #0d1b3e; border: 1px solid #cbd5e1; }
            .hint { width: 100%; text-align: center; color: #475569; font-size: 12px; }
            .sheet { display: grid; grid-template-columns: repeat(2, 54mm); place-content: center; align-items: start; gap: 18px; padding: 22px; }
            .badge {
              width: 54mm;
              height: 85.6mm;
              border-radius: 3.2mm;
              overflow: hidden;
              position: relative;
              color: #fff;
              box-shadow: 0 18px 45px rgba(0,0,0,.24);
              border: .35mm solid #f5a623;
            }
            .front { background: linear-gradient(145deg, #07162f 0%, #0d1b3e 62%, #123d5a 100%); }
            .front .wave { position: absolute; left: -12mm; right: -14mm; bottom: -13mm; height: 30mm; background: rgba(18,168,107,.96); transform: rotate(-8deg); border-radius: 50%; }
            .front .top { position: relative; z-index: 1; display: grid; justify-items: center; gap: 1.05mm; padding: 6mm 4.5mm 0; text-align: center; }
            .brand { font-weight: 950; font-size: 6.4mm; letter-spacing: .1mm; line-height: .95; text-align: center; }
            .tag { color: #f5a623; font-size: 1.85mm; font-weight: 900; text-transform: uppercase; margin-top: .2mm; line-height: 1.15; max-width: 42mm; text-align: center; }
            .role { color: #ffffff; background: rgba(255,255,255,.12); border: .2mm solid rgba(255,255,255,.28); border-radius: 10mm; padding: 1.25mm 3mm; font-size: 2.15mm; font-weight: 950; line-height: 1; text-align: center; max-width: 42mm; white-space: nowrap; }
            .front .content { position: relative; z-index: 1; display: grid; justify-items: center; gap: 2mm; padding: 5mm 4.5mm 0; text-align: center; }
            .photo { width: 27mm; height: 32mm; border-radius: 4mm; overflow: hidden; background: #fff; color: #0d1b3e; display: grid; place-items: center; font-size: 8mm; font-weight: 950; border: .6mm solid rgba(255,255,255,.8); }
            .photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
            .name { font-size: 3.8mm; line-height: 1.03; font-weight: 950; text-transform: uppercase; max-width: 43mm; text-wrap: balance; }
            .city { color: rgba(255,255,255,.88); font-size: 2.55mm; font-weight: 850; margin-top: .4mm; }
            .code { display: inline-block; background: #f5a623; color: #07162f; border-radius: 2mm; padding: 1.1mm 2.2mm; font-size: 2.95mm; font-weight: 950; letter-spacing: .25mm; margin-top: .8mm; }
            .front .bottom { position: absolute; z-index: 2; left: 4mm; right: 4mm; bottom: 3.2mm; display: grid; gap: .8mm; text-align: center; font-size: 2.35mm; font-weight: 850; color: #fff; }
            .phone { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .back { background: #ffffff; color: #0d1b3e; border-color: #0d1b3e; display: grid; grid-template-rows: auto auto auto auto; justify-items: center; align-content: center; gap: 3.4mm; padding: 6mm 5mm; text-align: center; }
            .back .brandSmall { font-size: 5.2mm; font-weight: 950; color: #0d1b3e; }
            .back h2 { margin: 0; font-size: 4.8mm; line-height: 1.05; color: #0d1b3e; }
            .back p { margin: 0; font-size: 2.65mm; line-height: 1.22; color: #23364d; }
            .back .qr { width: 31mm; height: 31mm; background: #fff; border: .3mm solid #e2e8f0; border-radius: 2mm; padding: 1.2mm; }
            .back .qr img { width: 100%; height: 100%; display: block; }
            .back .code2 { color: #12a86b; font-weight: 950; font-size: 3mm; }
            .back .link { font-size: 2.1mm; color: #0d1b3e; word-break: break-all; }
            .only-front, .only-back { display: contents; }
            @page { size: 54mm 85.6mm; margin: 0; }
            @media print {
              html, body { width: 54mm; min-height: 85.6mm; background: #fff; }
              .toolbar { display: none; }
              .sheet { display: block; padding: 0; }
              .badge { box-shadow: none; border-radius: 0; border: 0; break-after: page; page-break-after: always; }
              body.print-front .only-back { display: none; }
              body.print-back .only-front { display: none; }
              body.print-all .badge { break-after: page; page-break-after: always; }
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <button class="primary" onclick="document.body.className='print-front'; window.print()">Imprimer recto</button>
            <button class="secondary" onclick="document.body.className='print-back'; window.print()">Imprimer verso</button>
            <button class="light" onclick="document.body.className='print-all'; window.print()">Imprimer les deux</button>
            <button class="light" onclick="window.close()">Fermer</button>
            <div class="hint">PVC CR80 portrait: imprimez d'abord le recto, retournez les cartes, puis imprimez le verso.</div>
          </div>
          <div class="sheet">
            <div class="only-front">
              <section class="badge front">
                <div class="wave"></div>
                <div class="top">
                  <div class="brand">TOKKAM</div>
                  <div class="role">AGENT COMMERCIAL</div>
                  <div class="tag">Protect · Connect · Reassure</div>
                </div>
                <div class="content">
                  <div class="photo">${photo ? `<img src="${photo}" alt="Photo ${name}" />` : initials}</div>
                  <div>
                    <div class="name">${name}</div>
                    <div class="city">${city}</div>
                    <div class="code">${agent.code}</div>
                  </div>
                </div>
                <div class="bottom">
                  <div class="phone">${phone || 'TOKKAM'}</div>
                </div>
              </section>
            </div>
            <div class="only-back">
              <section class="badge back">
                <div class="brandSmall">TOKKAM</div>
                <h2>Installer TOKKAM</h2>
                <div class="qr"><img src="${qr}" alt="QR ${agent.code}" /></div>
                <p>Scannez ce QR code pour télécharger l'application et rattacher l'installation à cet agent.</p>
                <div class="code2">Code agent: ${agent.code}</div>
                <div class="link">${link}</div>
              </section>
            </div>
          </div>
        </body>
      </html>`);
    win.document.close();
  }

  function printAgentCard(agent) {
    const link = installLink(agent.code);
    const qr = qrUrl(agent.code, 260);
    const win = window.open('', '_blank', 'width=620,height=840');
    if (!win) {
      toast.error('Autorisez les popups pour imprimer la fiche.');
      return;
    }
    win.document.write(`<!doctype html>
      <html lang="fr">
        <head>
          <meta charset="utf-8" />
          <title>Fiche agent ${agent.code}</title>
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 32px; font-family: Arial, sans-serif; color: #0d1b3e; background: #f4f7fb; }
            .card { width: 100%; max-width: 520px; margin: 0 auto; background: #fff; border: 2px solid #0d1b3e; border-radius: 18px; padding: 28px; text-align: center; }
            .brand { font-size: 30px; font-weight: 900; letter-spacing: 0; margin-bottom: 6px; }
            .tag { color: #f5a623; font-weight: 800; text-transform: uppercase; font-size: 12px; letter-spacing: .08em; margin-bottom: 18px; }
            h1 { margin: 0 0 6px; font-size: 24px; }
            .agent { font-size: 15px; color: #4b5563; margin-bottom: 20px; }
            img { width: 260px; height: 260px; border: 1px solid #d8dee9; border-radius: 12px; padding: 10px; }
            .code { margin: 18px auto 10px; display: inline-block; background: #0d1b3e; color: #fff; border-radius: 12px; padding: 12px 18px; font-weight: 900; font-size: 22px; letter-spacing: .04em; }
            .link { word-break: break-all; color: #1e4db7; font-size: 13px; margin: 12px 0 22px; }
            .steps { text-align: left; background: #f8fafc; border-radius: 14px; padding: 16px; line-height: 1.55; font-size: 14px; }
            .foot { margin-top: 18px; color: #64748b; font-size: 12px; }
            @media print { body { background: #fff; padding: 0; } .card { border-radius: 0; border: none; } }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="brand">TOKKAM</div>
            <div class="tag">Protect · Connect · Reassure</div>
            <h1>Scannez pour installer TOKKAM</h1>
            <div class="agent">${agent.full_name || agent.code}${agent.city ? ' · ' + agent.city : ''}</div>
            <img src="${qr}" alt="QR code TOKKAM ${agent.code}" />
            <div class="code">${agent.code}</div>
            <div class="link">${link}</div>
            <div class="steps">
              <strong>Comment faire :</strong><br/>
              1. Scannez le QR code avec le téléphone.<br/>
              2. Téléchargez TOKKAM sur Android ou iPhone.<br/>
              3. Créez le compte parent avec le code agent affiché.
            </div>
            <div class="foot">Fiche générée depuis le backoffice commercial TOKKAM.</div>
          </div>
          <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
        </body>
      </html>`);
    win.document.close();
  }

  function editAgent(agent) {
    setAgentForm({
      code: agent.code || '',
      fullName: agent.full_name || agent.fullName || '',
      phoneNumber: agent.phone_number || '',
      city: agent.city || '',
      supervisor: agent.supervisor || '',
      status: agent.status || 'active',
      photoUrl: agent.photo_url || agent.photoUrl || '',
      photoDataUrl: '',
      removePhoto: false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Terrain commercial</h1>
          <p style={S.subtitle}>Suivi des agents, prospects, installations, activations et clients payants.</p>
        </div>
        <button style={S.refreshBtn} onClick={loadDashboard} disabled={loading}>{loading ? 'Lecture...' : 'Rafraichir'}</button>
      </div>

      <div style={S.statsGrid}>
        <StatCard label="Agents actifs" value={summary.agentsActive} hint={`${summary.agentsTotal || 0} agents total`} />
        <StatCard label="Leads 7 jours" value={summary.leads7d} hint={`${summary.leadsToday || 0} aujourd'hui`} tone="gold" />
        <StatCard label="Clics QR" value={summary.referralClicks} hint={`${summary.referralStarts || 0} inscriptions demarrees`} />
        <StatCard label="Installations" value={summary.installed} hint="Statut declare terrain" />
        <StatCard label="Activations" value={summary.activated} hint={`${summary.paid || 0} clients payants`} tone="green" />
        <StatCard label="Inscriptions app" value={summary.referralRegistrations} hint={`${summary.referralStarts || 0} demarrages`} tone="green" />
        <StatCard label="Actions clients" value={(summary.appChildren || 0) + (summary.appTrackers || 0)} hint={`${summary.appSubscriptionsPaid || 0} abonnements payes`} tone="gold" />
      </div>

      <div style={S.grid}>
        <form style={S.card} onSubmit={saveAgent}>
          <h2 style={S.cardTitle}>Creer / modifier un agent</h2>
          <label style={S.field}><span style={S.label}>Code agent</span><input style={S.input} value={agentForm.code} onChange={(e) => setAgentForm({ ...agentForm, code: e.target.value.toUpperCase() })} placeholder="AG-YDE-001" /></label>
          <label style={S.field}><span style={S.label}>Nom complet</span><input style={S.input} value={agentForm.fullName} onChange={(e) => setAgentForm({ ...agentForm, fullName: e.target.value })} placeholder="Nom de l'agent" /></label>
          <div style={S.twoCols}>
            <label style={S.field}><span style={S.label}>Telephone</span><input style={S.input} value={agentForm.phoneNumber} onChange={(e) => setAgentForm({ ...agentForm, phoneNumber: e.target.value })} placeholder="6..." /></label>
            <label style={S.field}><span style={S.label}>Ville</span><input style={S.input} value={agentForm.city} onChange={(e) => setAgentForm({ ...agentForm, city: e.target.value })} placeholder="Yaounde" /></label>
          </div>
          <label style={S.field}><span style={S.label}>Superviseur</span><input style={S.input} value={agentForm.supervisor} onChange={(e) => setAgentForm({ ...agentForm, supervisor: e.target.value })} /></label>
          <label style={S.field}><span style={S.label}>Statut</span><select style={S.input} value={agentForm.status} onChange={(e) => setAgentForm({ ...agentForm, status: e.target.value })}><option value="active">Actif</option><option value="paused">En pause</option><option value="archived">Archive</option></select></label>
          <label style={S.field}>
            <span style={S.label}>Photo de l'agent</span>
            <div style={S.photoUploadRow}>
              <div style={S.photoPreview}>
                {(agentForm.photoDataUrl || (!agentForm.removePhoto && agentForm.photoUrl))
                  ? <img style={S.photoPreviewImg} src={agentForm.photoDataUrl || agentForm.photoUrl} alt="Photo agent" />
                  : <span>{agentPhotoInitials(agentForm.fullName || agentForm.code)}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <input style={S.input} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => readAgentPhoto(e.target.files?.[0])} />
                <div style={S.muted}>JPG, PNG ou WebP. Compression carree 600x600 pour badge.</div>
                <div style={S.actionRow}>
                  {(agentForm.photoDataUrl || agentForm.photoUrl) && <button type="button" style={S.smallBtn} onClick={() => setAgentForm((current) => ({ ...current, photoDataUrl: '', removePhoto: Boolean(current.photoUrl) }))}>Supprimer la photo</button>}
                  {agentForm.photoDataUrl && <button type="button" style={S.smallBtn} onClick={() => setAgentForm((current) => ({ ...current, photoDataUrl: '', removePhoto: false }))}>Annuler remplacement</button>}
                </div>
              </div>
            </div>
          </label>
          <button style={S.primaryBtn} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer agent'}</button>
        </form>

        <form style={S.card} onSubmit={saveLead}>
          <h2 style={S.cardTitle}>Ajouter un prospect terrain</h2>
          <label style={S.field}><span style={S.label}>Agent</span><select style={S.input} value={leadForm.agentCode} onChange={(e) => setLeadForm({ ...leadForm, agentCode: e.target.value })}>{activeAgents.map((agent) => <option key={agent.code} value={agent.code}>{agent.code} - {agent.full_name}</option>)}</select></label>
          <div style={S.twoCols}>
            <label style={S.field}><span style={S.label}>Type cible</span><select style={S.input} value={leadForm.targetType} onChange={(e) => setLeadForm({ ...leadForm, targetType: e.target.value })}>{Object.entries(TARGET_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
            <label style={S.field}><span style={S.label}>Interet</span><select style={S.input} value={leadForm.interestLevel} onChange={(e) => setLeadForm({ ...leadForm, interestLevel: e.target.value })}><option value="hot">Chaud</option><option value="warm">Tiede</option><option value="cold">Froid</option></select></label>
          </div>
          <label style={S.field}><span style={S.label}>Nom prospect</span><input style={S.input} value={leadForm.prospectName} onChange={(e) => setLeadForm({ ...leadForm, prospectName: e.target.value })} placeholder="Parent, ecole ou entreprise" /></label>
          <div style={S.twoCols}>
            <label style={S.field}><span style={S.label}>Telephone</span><input style={S.input} value={leadForm.phoneNumber} onChange={(e) => setLeadForm({ ...leadForm, phoneNumber: e.target.value })} /></label>
            <label style={S.field}><span style={S.label}>Ville</span><input style={S.input} value={leadForm.city} onChange={(e) => setLeadForm({ ...leadForm, city: e.target.value })} /></label>
          </div>
          <div style={S.twoCols}>
            <label style={S.field}><span style={S.label}>Quartier</span><input style={S.input} value={leadForm.neighborhood} onChange={(e) => setLeadForm({ ...leadForm, neighborhood: e.target.value })} /></label>
            <label style={S.field}><span style={S.label}>Source</span><input style={S.input} value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })} placeholder="Ecole, stand, WhatsApp..." /></label>
          </div>
          <label style={S.field}><span style={S.label}>Notes</span><textarea style={S.textarea} value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} /></label>
          <button style={S.primaryBtn} disabled={saving || !activeAgents.length}>{activeAgents.length ? 'Enregistrer prospect' : 'Creer un agent d abord'}</button>
        </form>
      </div>


      <div style={S.card}>
        <h2 style={S.cardTitle}>Generateur de badges employes</h2>
        <p style={S.sectionHint}>Creez un badge PVC CR80 pour les employes TOKKAM selon leur role, sans creer un code agent commercial.</p>
        <div style={S.employeeBadgeGrid}>
          <label style={S.field}><span style={S.label}>Nom complet</span><input style={S.input} value={employeeBadgeForm.fullName} onChange={(e) => setEmployeeBadgeForm({ ...employeeBadgeForm, fullName: e.target.value })} placeholder="Nom de l employe" /></label>
          <label style={S.field}><span style={S.label}>Role</span><select style={S.input} value={employeeBadgeForm.role} onChange={(e) => setEmployeeBadgeForm({ ...employeeBadgeForm, role: e.target.value })}>{EMPLOYEE_BADGE_ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}</select></label>
          <label style={S.field}><span style={S.label}>Departement</span><input style={S.input} value={employeeBadgeForm.department} onChange={(e) => setEmployeeBadgeForm({ ...employeeBadgeForm, department: e.target.value })} placeholder="Ex: Operations, Support, Technique" /></label>
          <label style={S.field}><span style={S.label}>Matricule / ID</span><input style={S.input} value={employeeBadgeForm.employeeId} onChange={(e) => setEmployeeBadgeForm({ ...employeeBadgeForm, employeeId: e.target.value.toUpperCase() })} placeholder="TKM-001" /></label>
          <label style={S.field}><span style={S.label}>Telephone</span><input style={S.input} value={employeeBadgeForm.phoneNumber} onChange={(e) => setEmployeeBadgeForm({ ...employeeBadgeForm, phoneNumber: e.target.value })} placeholder="6..." /></label>
          <label style={S.field}><span style={S.label}>Ville</span><input style={S.input} value={employeeBadgeForm.city} onChange={(e) => setEmployeeBadgeForm({ ...employeeBadgeForm, city: e.target.value })} placeholder="Yaounde" /></label>
          <label style={S.field}>
            <span style={S.label}>Photo</span>
            <div style={S.photoUploadRow}>
              <div style={S.photoPreview}>
                {employeeBadgeForm.photoDataUrl ? <img style={S.photoPreviewImg} src={employeeBadgeForm.photoDataUrl} alt="Photo employe" /> : <span>{employeeBadgeInitials(employeeBadgeForm.fullName)}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <input style={S.input} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => readEmployeeBadgePhoto(e.target.files?.[0])} />
                <div style={S.muted}>Utilisee uniquement pour l'impression du badge.</div>
              </div>
            </div>
          </label>
          <div style={S.employeeBadgeActions}>
            <button type="button" style={S.primaryBtn} onClick={printEmployeeBadge}>Generer / imprimer le badge</button>
            <button type="button" style={S.smallBtn} onClick={() => setEmployeeBadgeForm({ fullName: '', role: 'commercial', department: '', phoneNumber: '', employeeId: '', city: 'Yaounde', photoDataUrl: '' })}>Vider</button>
          </div>
        </div>
      </div>

      <div style={S.gridWide}>
        <div style={S.card}>
          <h2 style={S.cardTitle}>Performance agents</h2>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Agent</th><th style={S.th}>Ville</th><th style={S.th}>Lien</th><th style={S.th}>Leads</th><th style={S.th}>QR</th><th style={S.th}>App</th><th style={S.th}>Clients</th><th style={S.th}>Revenus</th><th style={S.th}>Conv.</th><th style={S.th}>Inst.</th><th style={S.th}>Activ.</th><th style={S.th}>Payants</th><th style={S.th}>Action</th></tr></thead>
              <tbody>
                {agents.map((agent) => (
                  <tr key={agent.code}>
                    <td style={S.td}>
                      <div style={S.agentCell}>
                        <div style={S.agentAvatar}>
                          {agent.photo_url ? <img style={S.agentAvatarImg} src={agent.photo_url} alt={`Photo ${agent.full_name || agent.code}`} /> : <span>{agentPhotoInitials(agent.full_name || agent.code)}</span>}
                        </div>
                        <div><b>{agent.code}</b><br/><span style={S.muted}>{agent.full_name}</span></div>
                      </div>
                    </td>
                    <td style={S.td}>{agent.city || 'n/a'}</td>
                    <td style={S.td}>
                      <div style={S.linkCell}>
                        <img style={S.qrThumb} src={qrUrl(agent.code, 96)} alt={`QR ${agent.code}`} />
                        <div>
                          <div style={S.actionRow}>
                            <button style={S.smallBtn} onClick={() => copyInstallLink(agent.code)}>Copier</button>
                            <button style={S.smallBtn} onClick={() => printAgentCard(agent)}>Fiche QR</button>
                            <button style={S.smallBtn} onClick={() => printPvcBadge(agent)}>Badge PVC</button>
                          </div>
                          <span style={S.muted}>{installLink(agent.code)}</span>
                        </div>
                      </div>
                    </td>
                    <td style={S.tdNumber}>{agent.leads_count || 0}<br/><span style={S.muted}>{agent.leads_7d || 0} / 7j</span></td>
                    <td style={S.tdNumber}>{agent.referral_clicks || 0}<br/><span style={S.muted}>clics</span></td>
                    <td style={S.tdNumber}>{agent.referral_registrations || 0}<br/><span style={S.muted}>{agent.referral_starts || 0} debuts</span></td>
                    <td style={S.tdNumber}>{agent.app_children || 0}<br/><span style={S.muted}>{agent.app_trackers || 0} traceurs</span></td>
                    <td style={S.tdNumber}>{agent.app_subscriptions_paid || 0}<br/><span style={S.muted}>{agent.app_sms_paid || 0} SMS</span></td>
                    <td style={S.tdNumber}>{formatRate(agent.referral_registrations, agent.referral_clicks)}<br/><span style={S.muted}>app / QR</span></td>
                    <td style={S.tdNumber}>{agent.installs_count || 0}</td>
                    <td style={S.tdNumber}>{agent.activations_count || 0}</td>
                    <td style={S.tdNumber}>{agent.paid_count || 0}</td>
                    <td style={S.td}>
                      <div style={S.actionStack}>
                        <button style={S.smallBtn} onClick={() => editAgent(agent)}>Modifier</button>
                        <button style={S.smallBtn} onClick={() => printAgentCard(agent)}>Imprimer</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!agents.length && <tr><td colSpan="13" style={S.empty}>Aucun agent cree.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={S.paginationRow}>
            <button
              type="button"
              style={{ ...S.paginationBtn, opacity: referralEventPagination.hasPrevious && !referralEventsLoading ? 1 : .5 }}
              disabled={!referralEventPagination.hasPrevious || referralEventsLoading}
              onClick={() => setReferralEventPage((page) => Math.max(1, page - 1))}
            >
              ← Precedent
            </button>
            <span style={S.pageIndicator}>
              {referralEventPagination.total ? `${referralEventPagination.page} / ${referralEventPagination.totalPages}` : `Page ${referralEventPagination.page || 1}`}
            </span>
            <button
              type="button"
              style={{ ...S.paginationBtn, opacity: referralEventPagination.hasNext && !referralEventsLoading ? 1 : .5 }}
              disabled={!referralEventPagination.hasNext || referralEventsLoading}
              onClick={() => setReferralEventPage((page) => page + 1)}
            >
              Suivant →
            </button>
          </div>
        </div>

        <div style={S.card}>
          <h2 style={S.cardTitle}>Derniers evenements QR et inscriptions</h2>
          <div style={S.referralToolbar}>
            <div style={S.searchBlock}>
              <label style={S.label} htmlFor="referralEventSearch">Rechercher un agent ou un telephone</label>
              <div style={S.searchRow}>
                <input
                  id="referralEventSearch"
                  style={S.searchInput}
                  value={referralEventSearch}
                  onChange={(event) => setReferralEventSearch(event.target.value)}
                  placeholder="Nom, code agent ou telephone"
                  aria-label="Rechercher un agent ou un telephone"
                />
                <span style={S.searchIcon} aria-hidden="true">🔍</span>
                {referralEventSearch && (
                  <button
                    type="button"
                    style={S.clearSearchBtn}
                    onClick={() => setReferralEventSearch('')}
                    aria-label="Effacer la recherche"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div style={S.limitBlock}>
              <label style={S.label} htmlFor="referralEventLimit">Afficher</label>
              <div style={S.limitRow}>
                <select
                  id="referralEventLimit"
                  style={S.limitSelect}
                  value={referralEventLimit}
                  onChange={(event) => {
                    const nextLimit = Number(event.target.value);
                    setReferralEventLimit(REFERRAL_EVENT_LIMIT_OPTIONS.includes(nextLimit) ? nextLimit : 10);
                    setReferralEventPage(1);
                  }}
                  aria-label="Nombre d'evenements a afficher"
                >
                  {REFERRAL_EVENT_LIMIT_OPTIONS.map((limit) => (
                    <option key={limit} value={limit}>{limit}</option>
                  ))}
                </select>
                <span style={S.limitText}>evenements</span>
              </div>
            </div>
          </div>
          <div style={S.searchHint}>
            {referralEventsLoading ? 'Recherche en cours...' : `${referralEventLimit} evenements maximum, du plus recent au plus ancien.`}
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Date</th><th style={S.th}>Agent</th><th style={S.th}>Evenement</th><th style={S.th}>Telephone</th><th style={S.th}>Plateforme</th><th style={S.th}>Source</th></tr></thead>
              <tbody>
                {recentReferralEvents.map((event) => (
                  <tr key={event.id}>
                    <td style={S.td}>{formatDate(event.created_at)}</td>
                    <td style={S.td}><b>{event.agent_code || 'n/a'}</b><br/><span style={S.muted}>{event.agent_name || ''}</span></td>
                    <td style={S.td}><span style={S.badge}>{REFERRAL_LABELS[event.event_type] || event.event_type}</span></td>
                    <td style={S.td}>{event.phone_number || 'n/a'}</td>
                    <td style={S.td}>{event.platform || 'n/a'}</td>
                    <td style={S.td}>{event.source || 'n/a'}</td>
                  </tr>
                ))}
                {!recentReferralEvents.length && <tr><td colSpan="6" style={S.empty}>Aucun evenement trouve.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div style={S.card}>
          <h2 style={S.cardTitle}>Pipeline prospects recent</h2>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Prospect</th><th style={S.th}>Agent</th><th style={S.th}>Ville</th><th style={S.th}>Statut</th><th style={S.th}>MAJ</th><th style={S.th}>Action</th></tr></thead>
              <tbody>
                {recentLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td style={S.td}><b>{lead.prospect_name}</b><br/><span style={S.muted}>{TARGET_LABELS[lead.target_type] || lead.target_type} · {lead.phone_number || 'telephone n/a'}</span></td>
                    <td style={S.td}>{lead.agent_code}<br/><span style={S.muted}>{lead.agent_name || ''}</span></td>
                    <td style={S.td}>{lead.city || 'n/a'}<br/><span style={S.muted}>{lead.neighborhood || ''}</span></td>
                    <td style={S.td}><span style={S.badge}>{STATUS_LABELS[lead.status] || lead.status}</span></td>
                    <td style={S.td}>{formatDate(lead.updated_at || lead.created_at)}</td>
                    <td style={S.td}>
                      <select style={S.smallSelect} value={lead.status} onChange={(e) => updateLeadStatus(lead, e.target.value)}>
                        {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {!recentLeads.length && <tr><td colSpan="6" style={S.empty}>Aucun prospect enregistre.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: 20, color: '#ffffff' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 18 },
  title: { margin: 0, fontSize: 24, letterSpacing: .2 },
  subtitle: { margin: '6px 0 0', color: 'rgba(255,255,255,.82)', fontSize: 12 },
  refreshBtn: { background: '#1e4db7', color: '#ffffff', border: 'none', borderRadius: 6, padding: '9px 14px', fontWeight: 800, cursor: 'pointer' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 12, marginBottom: 16 },
  statCard: { background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 8, padding: 16 },
  statLabel: { color: 'rgba(255,255,255,.82)', fontSize: 11, textTransform: 'uppercase', letterSpacing: .7, fontWeight: 800 },
  statValue: { fontSize: 30, fontWeight: 900, marginTop: 8 },
  statHint: { color: 'rgba(255,255,255,.72)', fontSize: 11, marginTop: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(320px,420px) 1fr', gap: 16, marginBottom: 16 },
  gridWide: { display: 'grid', gridTemplateColumns: '1fr', gap: 16 },
  card: { background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 8, padding: 16, boxShadow: '0 18px 40px rgba(0,0,0,.16)' },
  cardTitle: { margin: '0 0 14px', fontSize: 15, color: '#ffffff' },
  twoCols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  field: { display: 'grid', gap: 7, marginBottom: 13 },
  label: { fontSize: 11, color: '#ffffff', textTransform: 'uppercase', letterSpacing: .7, fontWeight: 800 },
  input: { width: '100%', background: '#0d1b3e', border: '1px solid rgba(255,255,255,.22)', color: '#ffffff', borderRadius: 6, padding: '10px 12px', outline: 'none', fontFamily: 'monospace', fontSize: 13 },
  textarea: { width: '100%', minHeight: 76, background: '#0d1b3e', border: '1px solid rgba(255,255,255,.22)', color: '#ffffff', borderRadius: 6, padding: '10px 12px', outline: 'none', fontFamily: 'monospace', fontSize: 13 },
  primaryBtn: { width: '100%', background: '#f5a623', color: '#0d1b3e', border: 'none', borderRadius: 6, padding: '11px 14px', fontWeight: 900, cursor: 'pointer' },
  photoUploadRow: { display: 'flex', alignItems: 'center', gap: 12 },
  photoPreview: { width: 76, height: 76, borderRadius: 14, background: '#0d1b3e', border: '1px solid rgba(255,255,255,.22)', color: '#f5a623', display: 'grid', placeItems: 'center', overflow: 'hidden', fontWeight: 950, fontSize: 20, flex: '0 0 auto' },
  photoPreviewImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  employeeBadgeGrid: { display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 },
  employeeBadgeActions: { display: 'grid', alignContent: 'end', gap: 8 },
  sectionHint: { color: 'rgba(255,255,255,.72)', fontSize: 12, marginTop: -6, marginBottom: 14 },
  referralToolbar: { display: 'flex', alignItems: 'end', flexWrap: 'wrap', gap: 12, marginBottom: 8 },
  searchBlock: { display: 'grid', gap: 7, flex: '1 1 320px', maxWidth: 560 },
  searchRow: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchInput: { width: '100%', background: '#0d1b3e', border: '1px solid rgba(255,255,255,.22)', color: '#ffffff', borderRadius: 6, padding: '10px 64px 10px 12px', outline: 'none', fontFamily: 'monospace', fontSize: 13 },
  searchIcon: { position: 'absolute', right: 34, pointerEvents: 'none', fontSize: 15 },
  clearSearchBtn: { position: 'absolute', right: 7, width: 22, height: 22, borderRadius: 999, border: '1px solid rgba(255,255,255,.22)', background: 'rgba(255,255,255,.08)', color: '#ffffff', cursor: 'pointer', lineHeight: '18px', fontWeight: 900 },
  limitBlock: { display: 'grid', gap: 7, flex: '0 0 auto' },
  limitRow: { display: 'flex', alignItems: 'center', gap: 8 },
  limitSelect: { background: '#0d1b3e', border: '1px solid rgba(255,255,255,.22)', color: '#ffffff', borderRadius: 6, padding: '10px 28px 10px 12px', outline: 'none', fontFamily: 'monospace', fontSize: 13, fontWeight: 800 },
  limitText: { color: 'rgba(255,255,255,.82)', fontSize: 12, fontWeight: 800 },
  searchHint: { color: 'rgba(255,255,255,.68)', fontSize: 11, marginBottom: 12 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  paginationRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 12 },
  paginationBtn: { background: 'rgba(255,255,255,.08)', color: '#ffffff', border: '1px solid rgba(255,255,255,.18)', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontWeight: 800 },
  pageIndicator: { color: '#f5a623', fontSize: 12, fontWeight: 900 },
  th: { color: '#ffffff', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, borderBottom: '1px solid rgba(255,255,255,.16)', padding: '10px 12px' },
  td: { color: '#ffffff', borderBottom: '1px solid rgba(255,255,255,.10)', padding: '11px 12px', fontSize: 13, verticalAlign: 'top' },
  tdNumber: { color: '#f5a623', borderBottom: '1px solid rgba(255,255,255,.10)', padding: '11px 12px', fontSize: 13, fontWeight: 900, textAlign: 'center' },
  muted: { color: 'rgba(255,255,255,.68)', fontSize: 11 },
  badge: { display: 'inline-block', background: 'rgba(245,166,35,.13)', color: '#f5a623', border: '1px solid rgba(245,166,35,.32)', borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 800 },
  agentCell: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 170 },
  agentAvatar: { width: 38, height: 38, borderRadius: 12, background: '#0d1b3e', border: '1px solid rgba(255,255,255,.18)', color: '#f5a623', display: 'grid', placeItems: 'center', overflow: 'hidden', fontSize: 12, fontWeight: 950, flex: '0 0 auto' },
  agentAvatarImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  linkCell: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 260 },
  qrThumb: { width: 54, height: 54, borderRadius: 6, background: '#ffffff', padding: 4 },
  actionRow: { display: 'flex', gap: 6, marginBottom: 6 },
  actionStack: { display: 'grid', gap: 6 },
  smallBtn: { background: 'rgba(255,255,255,.08)', color: '#ffffff', border: '1px solid rgba(255,255,255,.18)', borderRadius: 5, padding: '6px 10px', cursor: 'pointer' },
  smallSelect: { background: '#0d1b3e', color: '#ffffff', border: '1px solid rgba(255,255,255,.22)', borderRadius: 5, padding: '6px 8px', fontFamily: 'monospace' },
  empty: { color: 'rgba(255,255,255,.86)', padding: 14, fontSize: 12 },
};
